import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Play, Pause, Volume2, VolumeX, Maximize, MessageCircle,
  Hand, Send, ThumbsUp, Languages, Accessibility, Users,
  Radio, CheckCircle, Mic, MicOff, Settings, X, User
} from 'lucide-react';
import api from '@/lib/api';
import { SIGN_LANGUAGES } from './SignLanguageInterpreter';
import LiveSignLanguagePanel from './LiveSignLanguagePanel';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Translation Panel Component
const TranslationPanel = ({ 
  originalText, 
  selectedLanguage, 
  onLanguageChange, 
  isVoiceEnabled,
  onVoiceToggle,
  translatedText 
}) => {
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'pt', name: 'Portuguese', flag: '🇧🇷' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
  ];

  return (
    <div className="bg-slate-50 rounded-lg p-4 space-y-3" data-testid="translation-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Languages className="h-5 w-5 text-blue-600" />
          <span className="font-medium">Live Translation</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="voice-translation"
              checked={isVoiceEnabled}
              onCheckedChange={onVoiceToggle}
            />
            <Label htmlFor="voice-translation" className="text-sm cursor-pointer">
              {isVoiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Label>
          </div>
          <Select value={selectedLanguage} onValueChange={onLanguageChange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map(lang => (
                <SelectItem key={lang.code} value={lang.code}>
                  <span className="flex items-center gap-2">
                    <span>{lang.flag}</span>
                    <span>{lang.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Translated text display */}
      <div className="bg-white rounded-lg p-3 border min-h-[60px]">
        {translatedText ? (
          <p className="text-lg">{translatedText}</p>
        ) : (
          <p className="text-slate-400 italic">Translation will appear here...</p>
        )}
      </div>
      
      {/* Original text (collapsed) */}
      {originalText && selectedLanguage !== 'en' && (
        <details className="text-sm">
          <summary className="text-slate-500 cursor-pointer hover:text-slate-700">
            Show original
          </summary>
          <p className="mt-2 text-slate-600 bg-slate-100 rounded p-2">{originalText}</p>
        </details>
      )}
    </div>
  );
};

// Accessibility Settings Panel
const AccessibilitySettingsPanel = ({ 
  settings, 
  onSettingsChange, 
  onClose 
}) => {
  return (
    <div className="absolute top-4 right-4 bg-white rounded-lg shadow-xl p-4 w-72 z-50" data-testid="accessibility-settings">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Accessibility className="h-5 w-5" />
          Accessibility
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="space-y-4">
        {/* Sign Language */}
        <div className="flex items-center justify-between">
          <div>
            <Label className="font-medium">Sign Language Avatar</Label>
            <p className="text-xs text-slate-500">AI-powered sign interpreter</p>
          </div>
          <Switch
            checked={settings.signLanguage}
            onCheckedChange={(v) => onSettingsChange({ ...settings, signLanguage: v })}
          />
        </div>
        
        {settings.signLanguage && (
          <Select 
            value={settings.signLanguageType} 
            onValueChange={(v) => onSettingsChange({ ...settings, signLanguageType: v })}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select sign language" />
            </SelectTrigger>
            <SelectContent>
              {SIGN_LANGUAGES.map(lang => (
                <SelectItem key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name} ({lang.region})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        {/* Live Captions */}
        <div className="flex items-center justify-between">
          <div>
            <Label className="font-medium">Live Captions</Label>
            <p className="text-xs text-slate-500">Real-time text transcription</p>
          </div>
          <Switch
            checked={settings.captions}
            onCheckedChange={(v) => onSettingsChange({ ...settings, captions: v })}
          />
        </div>
        
        {/* Caption Size */}
        {settings.captions && (
          <div>
            <Label className="text-sm">Caption Size</Label>
            <Select 
              value={settings.captionSize} 
              onValueChange={(v) => onSettingsChange({ ...settings, captionSize: v })}
            >
              <SelectTrigger className="w-full mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="large">Large</SelectItem>
                <SelectItem value="xlarge">Extra Large</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* High Contrast */}
        <div className="flex items-center justify-between">
          <div>
            <Label className="font-medium">High Contrast</Label>
            <p className="text-xs text-slate-500">Improve visibility</p>
          </div>
          <Switch
            checked={settings.highContrast}
            onCheckedChange={(v) => onSettingsChange({ ...settings, highContrast: v })}
          />
        </div>
        
        {/* Translation */}
        <div className="flex items-center justify-between">
          <div>
            <Label className="font-medium">Live Translation</Label>
            <p className="text-xs text-slate-500">Translate to your language</p>
          </div>
          <Switch
            checked={settings.translation}
            onCheckedChange={(v) => onSettingsChange({ ...settings, translation: v })}
          />
        </div>
      </div>
    </div>
  );
};

export const VirtualStage = ({ stageId, eventId, isAdmin = false }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('stream');
  const [question, setQuestion] = useState('');
  const [showAccessibilitySettings, setShowAccessibilitySettings] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const captionsRef = useRef(null);
  const speechSynthRef = useRef(null);
  
  // Accessibility settings
  const [accessibilitySettings, setAccessibilitySettings] = useState({
    signLanguage: false,
    signLanguageType: 'asl',
    captions: true,
    captionSize: 'medium',
    highContrast: false,
    translation: false
  });

  // Build API path based on whether eventId is provided
  const basePath = eventId ? `/events/${eventId}` : '';

  const { data: stage, isLoading } = useQuery({
    queryKey: ['stage', stageId],
    queryFn: () => api.get(`${basePath}/stages/${stageId}`).then(r => r.data),
    refetchInterval: 5000,
  });

  const { data: captions } = useQuery({
    queryKey: ['captions', stageId],
    queryFn: () => api.get(`${basePath}/stages/${stageId}/captions`).then(r => r.data).catch(() => []),
    refetchInterval: 2000,
    enabled: accessibilitySettings.captions,
  });

  const { data: questions } = useQuery({
    queryKey: ['questions', stageId],
    queryFn: () => api.get(`${basePath}/stages/${stageId}/questions`).then(r => r.data).catch(() => []),
    refetchInterval: 5000,
  });

  const { data: polls } = useQuery({
    queryKey: ['polls', stageId],
    queryFn: () => api.get(`${basePath}/stages/${stageId}/polls`).then(r => r.data).catch(() => []),
    refetchInterval: 3000,
  });

  const submitQuestionMutation = useMutation({
    mutationFn: (q) => api.post(`${basePath}/stages/${stageId}/questions`, null, { params: { question: q } }),
    onSuccess: () => {
      toast.success('Question submitted!');
      setQuestion('');
      queryClient.invalidateQueries(['questions', stageId]);
    },
    onError: () => toast.error('Failed to submit question')
  });

  const upvoteMutation = useMutation({
    mutationFn: (questionId) => api.post(`${basePath}/stages/${stageId}/questions/${questionId}/upvote`),
    onSuccess: () => queryClient.invalidateQueries(['questions', stageId]),
  });

  const votePollMutation = useMutation({
    mutationFn: ({ pollId, option }) => api.post(`${basePath}/stages/${stageId}/polls/${pollId}/vote`, null, { params: { option } }),
    onSuccess: () => {
      toast.success('Vote recorded!');
      queryClient.invalidateQueries(['polls', stageId]);
    },
  });

  // Translation mutation
  const translateMutation = useMutation({
    mutationFn: ({ text, targetLang }) => 
      api.post('/translate', { text, target_language: targetLang }).then(r => r.data),
    onSuccess: (data) => {
      setTranslatedText(data.translated_text || data.text);
      // Voice output if enabled
      if (voiceEnabled && data.translated_text) {
        speakText(data.translated_text, selectedLanguage);
      }
    },
    onError: () => {
      // Fallback: show original text
      setTranslatedText(captions?.[0]?.text || '');
    }
  });

  // Text-to-speech function
  const speakText = (text, lang) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      utterance.rate = 0.9;
      utterance.pitch = 1;
      
      // Find appropriate voice
      const voices = window.speechSynthesis.getVoices();
      const voice = voices.find(v => v.lang.startsWith(lang)) || voices[0];
      if (voice) utterance.voice = voice;
      
      window.speechSynthesis.speak(utterance);
    }
  };

  // Auto-scroll captions
  useEffect(() => {
    if (captionsRef.current) {
      captionsRef.current.scrollTop = captionsRef.current.scrollHeight;
    }
  }, [captions]);

  // Translate when captions update
  useEffect(() => {
    if (accessibilitySettings.translation && captions?.[0]?.text && selectedLanguage !== 'en') {
      translateMutation.mutate({ text: captions[0].text, targetLang: selectedLanguage });
    } else if (captions?.[0]?.text) {
      setTranslatedText(captions[0].text);
      if (voiceEnabled) {
        speakText(captions[0].text, selectedLanguage);
      }
    }
  }, [captions?.[0]?.text, selectedLanguage, accessibilitySettings.translation]);

  // Helper to convert YouTube/Vimeo URLs to embed URLs
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1&mute=0`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    }
    
    // Already an embed URL or other
    return url;
  };

  // Caption size styles
  const captionSizeClasses = {
    small: 'text-sm',
    medium: 'text-lg',
    large: 'text-xl',
    xlarge: 'text-2xl'
  };

  if (isLoading) {
    return <div className="animate-pulse h-96 bg-slate-200 rounded-lg" />;
  }

  const isLive = stage?.status === 'live' || stage?.is_live;
  const embedUrl = getEmbedUrl(stage?.stream_url);
  const currentCaption = captions?.[0]?.text || '';

  return (
    <div className={`space-y-4 ${accessibilitySettings.highContrast ? 'high-contrast' : ''}`} data-testid="virtual-stage">
      {/* Video Player Area */}
      <Card className="overflow-hidden relative">
        {/* Accessibility Settings Button */}
        <Button
          variant="outline"
          size="sm"
          className="absolute top-4 right-4 z-40 bg-white/90 backdrop-blur"
          onClick={() => setShowAccessibilitySettings(!showAccessibilitySettings)}
        >
          <Accessibility className="h-4 w-4 mr-2" />
          Accessibility
        </Button>
        
        {/* Accessibility Settings Panel */}
        {showAccessibilitySettings && (
          <AccessibilitySettingsPanel
            settings={accessibilitySettings}
            onSettingsChange={setAccessibilitySettings}
            onClose={() => setShowAccessibilitySettings(false)}
          />
        )}

        <div className={`relative ${accessibilitySettings.highContrast ? 'bg-black' : 'bg-slate-900'} aspect-video`}>
          {isLive && embedUrl ? (
            <div className="relative w-full h-full flex">
              {/* Main video stream */}
              <iframe
                src={embedUrl}
                className="w-full h-full"
                allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                allowFullScreen
                title="Live Stream"
              />
              
              {/* Live indicator */}
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <Badge variant="destructive" className="animate-pulse">
                  <Radio className="h-3 w-3 mr-1" /> LIVE
                </Badge>
                <Badge variant="secondary">
                  <Users className="h-3 w-3 mr-1" /> {stage?.viewer_count || 0}
                </Badge>
              </div>
              
              {/* Bottom controls */}
              <div className="absolute bottom-4 left-4 flex gap-2">
                <Button size="sm" variant="secondary">
                  <Maximize className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-white">
              <Play className="h-16 w-16 opacity-50 mb-4" />
              <p className="text-lg">Stream is offline</p>
              <p className="text-sm opacity-70">Check back when the session starts</p>
            </div>
          )}
        </div>
        
        {/* Captions Bar */}
        {accessibilitySettings.captions && (
          <div className={`${accessibilitySettings.highContrast ? 'bg-black text-yellow-300' : 'bg-slate-900 text-white'} p-3`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs opacity-70">Live Captions</span>
              <div className="flex items-center gap-2">
                {accessibilitySettings.translation && (
                  <Badge variant="outline" className="text-xs">
                    <Languages className="h-3 w-3 mr-1" />
                    {selectedLanguage.toUpperCase()}
                  </Badge>
                )}
                {voiceEnabled && (
                  <Badge variant="outline" className="text-xs">
                    <Volume2 className="h-3 w-3 mr-1" />
                    Audio
                  </Badge>
                )}
              </div>
            </div>
            <p className={`${captionSizeClasses[accessibilitySettings.captionSize]} leading-relaxed`}>
              {accessibilitySettings.translation && selectedLanguage !== 'en' 
                ? (translatedText || 'Translating...')
                : (currentCaption || 'Waiting for captions...')}
            </p>
          </div>
        )}
      </Card>

      {/* Translation Panel (when enabled) */}
      {accessibilitySettings.translation && (
        <TranslationPanel
          originalText={currentCaption}
          selectedLanguage={selectedLanguage}
          onLanguageChange={setSelectedLanguage}
          isVoiceEnabled={voiceEnabled}
          onVoiceToggle={setVoiceEnabled}
          translatedText={translatedText}
        />
      )}

      {/* Interactive Panel */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-2">
            <TabsList>
              <TabsTrigger value="stream">Info</TabsTrigger>
              <TabsTrigger value="qa">Q&A ({questions?.length || 0})</TabsTrigger>
              <TabsTrigger value="polls">Polls</TabsTrigger>
              <TabsTrigger value="captions">Full Captions</TabsTrigger>
            </TabsList>
          </CardHeader>
          
          <CardContent>
            <TabsContent value="stream" className="mt-0">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold">{stage?.name || 'Virtual Stage'}</h3>
                  <p className="text-sm text-slate-600">{stage?.description || 'Live streaming session'}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">
                    <Users className="h-3 w-3 mr-1" /> {stage?.viewer_count || 0} viewers
                  </Badge>
                  {accessibilitySettings.captions && (
                    <Badge variant="secondary">
                      <Languages className="h-3 w-3 mr-1" /> Captions On
                    </Badge>
                  )}
                  {accessibilitySettings.signLanguage && (
                    <Badge variant="secondary">
                      <Accessibility className="h-3 w-3 mr-1" /> Sign Language
                    </Badge>
                  )}
                  {accessibilitySettings.translation && (
                    <Badge variant="secondary">
                      <Languages className="h-3 w-3 mr-1" /> Translation
                    </Badge>
                  )}
                </div>
                
                {/* Quick accessibility toggles */}
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium mb-3">Quick Settings</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      variant={accessibilitySettings.signLanguage ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAccessibilitySettings(s => ({...s, signLanguage: !s.signLanguage}))}
                    >
                      <Accessibility className="h-4 w-4 mr-2" />
                      Sign Language
                    </Button>
                    <Button 
                      variant={accessibilitySettings.translation ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAccessibilitySettings(s => ({...s, translation: !s.translation}))}
                    >
                      <Languages className="h-4 w-4 mr-2" />
                      Translation
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="qa" className="mt-0">
              <div className="space-y-4">
                {/* Submit question */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Ask a question..."
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && question.trim() && submitQuestionMutation.mutate(question)}
                    data-testid="qa-input"
                  />
                  <Button
                    onClick={() => submitQuestionMutation.mutate(question)}
                    disabled={!question.trim() || submitQuestionMutation.isPending}
                    data-testid="qa-submit"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Questions list */}
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {questions?.map((q) => (
                      <div key={q.id} className="p-3 bg-slate-50 rounded-lg" data-testid={`question-${q.id}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">{q.delegate_name || 'Anonymous'}</p>
                            <p className="text-slate-700">{q.question}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => upvoteMutation.mutate(q.id)}
                            className="flex items-center gap-1"
                          >
                            <ThumbsUp className="h-4 w-4" />
                            {q.upvotes || 0}
                          </Button>
                        </div>
                        {q.answered && (
                          <Badge variant="secondary" className="mt-2">
                            <CheckCircle className="h-3 w-3 mr-1" /> Answered
                          </Badge>
                        )}
                      </div>
                    ))}
                    {(!questions || questions.length === 0) && (
                      <p className="text-center text-slate-500 py-8">No questions yet. Be the first to ask!</p>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </TabsContent>
            
            <TabsContent value="polls" className="mt-0">
              <div className="space-y-4">
                {polls?.filter(p => p.is_active).map((poll) => (
                  <div key={poll.id} className="p-4 border rounded-lg" data-testid={`poll-${poll.id}`}>
                    <h4 className="font-medium mb-3">{poll.question}</h4>
                    <div className="space-y-2">
                      {poll.options?.map((option) => {
                        const totalVotes = Object.values(poll.votes || {}).reduce((a, b) => a + b, 0);
                        const percentage = totalVotes > 0 ? ((poll.votes?.[option] || 0) / totalVotes) * 100 : 0;
                        return (
                          <button
                            key={option}
                            onClick={() => votePollMutation.mutate({ pollId: poll.id, option })}
                            className="w-full text-left p-3 border rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex justify-between mb-1">
                              <span>{option}</span>
                              <span className="text-slate-500">{poll.votes?.[option] || 0} votes</span>
                            </div>
                            <Progress value={percentage} className="h-2" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {(!polls || polls.filter(p => p.is_active).length === 0) && (
                  <p className="text-center text-slate-500 py-8">No active polls</p>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="captions" className="mt-0">
              <ScrollArea className="h-64" ref={captionsRef}>
                <div className="space-y-2">
                  {captions?.slice().reverse().map((cap, i) => (
                    <div key={cap.id || i} className="p-2 border-l-2 border-blue-500 pl-3">
                      <p className="text-xs text-slate-400">
                        {cap.timestamp ? new Date(cap.timestamp).toLocaleTimeString() : ''}
                        {cap.speaker && ` - ${cap.speaker}`}
                      </p>
                      <p>{cap.text}</p>
                    </div>
                  ))}
                  {(!captions || captions.length === 0) && (
                    <p className="text-center text-slate-500 py-8">No captions available yet</p>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>
      
      {/* High contrast styles */}
      <style jsx>{`
        .high-contrast {
          --foreground: #ffff00;
          --background: #000000;
        }
        .high-contrast .bg-slate-50 {
          background-color: #000 !important;
          color: #ffff00 !important;
          border: 2px solid #ffff00 !important;
        }
        .high-contrast .text-slate-600,
        .high-contrast .text-slate-500,
        .high-contrast .text-slate-700 {
          color: #ffff00 !important;
        }
      `}</style>

      {/* Sign Language Interpreter Panel - Now using LiveSignLanguagePanel */}
      {accessibilitySettings.signLanguage && (
        <LiveSignLanguagePanel
          position="bottom-right"
          size="medium"
          language={accessibilitySettings.signLanguageType}
          skinTone="medium"
          onClose={() => setAccessibilitySettings(s => ({...s, signLanguage: false}))}
          showSettings={true}
          externalText={currentCaption}
        />
      )}
    </div>
  );
};

export default VirtualStage;
